import { Controller } from "@hotwired/stimulus";

// Stimulus controller powering the custom video player on the home page.
// It mirrors a few native controls (play/pause, progress, fullscreen) while
// keeping the markup accessible and familiar.
export default class extends Controller {
  static targets = [
    "video", // <video> element that renders the media.
    "overlay", // Play overlay button that sits on top of the poster frame.
    "playToggle", // Play/pause button in the custom control bar.
    "progress", // Range slider that shows and changes playback progress.
    "fullscreen", // Fullscreen toggle button.
    "enterIcon", // Icon shown before fullscreen is active.
    "exitIcon" // Icon shown while fullscreen is active.
  ];

  connect() {
    this._bindHandlers();
    this._isScrubbing = false;

    const video = this.videoTarget;

    video.addEventListener("play", this._handlePlay);
    video.addEventListener("pause", this._handlePause);
    video.addEventListener("ended", this._handleEnded);
    video.addEventListener("timeupdate", this._handleTimeUpdate); // fired as the video plays
    video.addEventListener("loadedmetadata", this._handleLoadedMetadata); // fired when duration & dimensions are known
    video.addEventListener("seeked", this._handleSeeked); // fired when a programmatic seek completes

    document.addEventListener("fullscreenchange", this._handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this._handleFullscreenChange);

    this._attachWebkitFullscreenHooks();

    // When Turbo leaves the page in place, the video might already be ready.
    if (video.readyState >= 1) {
      this.handleLoadedMetadata();
    } else {
      this._prepareSlider();
    }

    this._updatePlayButton();
    this._updateFullscreenButton(false);
  }

  disconnect() {
    const video = this.videoTarget;

    video.removeEventListener("play", this._handlePlay);
    video.removeEventListener("pause", this._handlePause);
    video.removeEventListener("ended", this._handleEnded);
    video.removeEventListener("timeupdate", this._handleTimeUpdate);
    video.removeEventListener("loadedmetadata", this._handleLoadedMetadata);
    video.removeEventListener("seeked", this._handleSeeked);

    this._isScrubbing = false;

    document.removeEventListener("fullscreenchange", this._handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this._handleFullscreenChange);

    this._detachWebkitFullscreenHooks();
  }

  // Play the video when the overlay button is clicked.
  play() {
    this.videoTarget.play();
  }

  // Toggle between play and pause from either the video or custom control.
  toggle() {
    if (this.videoTarget.paused) {
      this.videoTarget.play();
    } else {
      this.videoTarget.pause();
    }
  }

  // Seek within the video as the slider moves.
  scrub(event) {

    // console.log("scrub", event);
    const duration = this._videoDuration();
    // console.log("duration", duration);
    const newTime = (this.progressTarget.value / 100) * duration;
    // console.log("newTime", newTime);
    this.videoTarget.currentTime = newTime;

    // const percent = Number(event.currentTarget.value) || 0;
    // const normalized = Math.min(Math.max(percent, 0), 100) / 100;
    // const newTime = duration * normalized;

    // console.log(this.progressTarget.value);

    // if (Math.abs(this.videoTarget.currentTime - newTime) < 0.01) {
    //   return;
    // }

    // this._isScrubbing = true;
    // this.videoTarget.currentTime = newTime;
    // this._updateSlider(newTime, duration);
  }

  // Allow the global keyboard shortcuts (space / k) to toggle playback.
  handleKeyUp(event) {
    if (event.defaultPrevented) return;

    const toggleKeys = [" ", "Space", "Spacebar", "k", "K"];
    if (!toggleKeys.includes(event.key)) return;

    const interactiveTags = ["INPUT", "TEXTAREA", "BUTTON", "SELECT"];
    if (interactiveTags.includes(event.target.tagName)) return;
    if (event.target.isContentEditable) return;

    event.preventDefault();
    this.toggle();
  }

  // Reset the UI after Turbo navigation so every visit starts fresh.
  handleTurboLoad() {
    this.videoTarget.pause();
    this.videoTarget.currentTime = 0;

    this._isScrubbing = false;
    this.overlayTarget.classList.remove("is-hidden");
    this._updatePlayButton();
    this._updateFullscreenButton(false);
    this.handleLoadedMetadata();
  }

  // Hide the overlay and refresh the play toggle when playback starts.
  handlePlay() {
    this.overlayTarget.classList.add("is-hidden");
    this._updatePlayButton();
  }

  // Show the overlay and refresh the play toggle when paused or ended.
  handlePause() {
    this.overlayTarget.classList.remove("is-hidden");
    this._updatePlayButton();
  }

  // Keep the slider in sync with the actual playback time.
  handleTimeUpdate() {
    if (this._isScrubbing) return;

    const duration = this._videoDuration();
    if (!duration) return;

    this._updateSlider(this.videoTarget.currentTime, duration);
  }

  // Prepare metadata once duration information becomes available.
  handleLoadedMetadata() {
    this._isScrubbing = false;

    const duration = this._videoDuration();

    this._prepareSlider(duration);
    this._updateSlider(this.videoTarget.currentTime || 0, duration);
  }

  // Clear scrubbing state once the browser finishes seeking.
  handleSeeked() {
    this._isScrubbing = false;

    const duration = this._videoDuration();
    if (!duration) return;

    this._updateSlider(this.videoTarget.currentTime, duration);
  }

  // React when the document's fullscreen state changes (ESC, native controls, etc.).
  handleFullscreenChange() {
    this._updateFullscreenButton();
  }

  // Safari/iOS emit custom fullscreen events on the video element itself.
  handleWebkitBeginFullscreen() {
    this._updateFullscreenButton(true);
  }

  handleWebkitEndFullscreen() {
    this._updateFullscreenButton(false);
  }

  // Toggle fullscreen for the video wrapper.
  toggleFullscreen(event) {
    if (event) event.preventDefault();

    if (this._isFullscreen()) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  // Request fullscreen with the right browser-specific API.
  enterFullscreen() {
    const container = this.element;

    if (container.requestFullscreen) {
      container.requestFullscreen().catch(function () {});
      return;
    }

    if (container.webkitRequestFullscreen) {
      container.webkitRequestFullscreen();
      return;
    }

    if (typeof this.videoTarget.webkitEnterFullscreen === "function") {
      this.videoTarget.webkitEnterFullscreen();
    }
  }

  // Exit fullscreen respecting the current browser's API.
  exitFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(function () {});
      return;
    }

    if (document.webkitFullscreenElement) {
      if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return;
    }

    if (typeof this.videoTarget.webkitExitFullscreen === "function") {
      this.videoTarget.webkitExitFullscreen();
    }
  }

  // --- Helper utilities --------------------------------------------------

  // Get the video's duration in seconds
  _videoDuration() {
    return this.videoTarget.duration || 0;
  }

  // Update the play/pause button icon and ARIA label.
  _updatePlayButton() {
    const isPlaying = !this.videoTarget.paused && !this.videoTarget.ended;
    this.playToggleTarget.textContent = isPlaying ? "❚❚" : "▶";
    this.playToggleTarget.setAttribute("aria-label", isPlaying ? "Pause video" : "Play video");
  }

  // Prepare the slider's attributes based on whether duration info is available.
  _prepareSlider(duration) {
    const slider = this.progressTarget;
    const hasDuration = duration > 0;

    slider.disabled = !hasDuration;
    slider.min = 0;
    slider.max = 100;
    slider.setAttribute("aria-valuemin", "0");
    slider.setAttribute("aria-valuemax", "100");

    if (!hasDuration) {
      slider.value = 0;
      slider.setAttribute("aria-valuenow", "0");
    }
  }

  _updateSlider(currentTime, duration) {
    const slider = this.progressTarget;
    const percent = duration > 0 ? (currentTime / duration) * 100 : 0;
    const clampedPercent = Math.max(0, Math.min(percent, 100));

    slider.value = clampedPercent;
    slider.setAttribute("aria-valuenow", clampedPercent.toFixed(1));
  }

  _updateFullscreenButton(forceState) {
    const isFull = typeof forceState === "boolean" ? forceState : this._isFullscreen();

    this.fullscreenTarget.setAttribute("aria-label", isFull ? "Exit full screen" : "Enter full screen");
    this.fullscreenTarget.setAttribute("aria-pressed", String(isFull));

    this.enterIconTarget.classList.toggle("is-hidden", isFull);
    this.exitIconTarget.classList.toggle("is-hidden", !isFull);
  }

  _isFullscreen() {
    const element = this.element;
    const fullscreenElement = document.fullscreenElement;
    const webkitFullscreenElement = document.webkitFullscreenElement;
    return fullscreenElement === element || webkitFullscreenElement === element;
  }

  // each DOM event uses a stable method reference that’s already bound to the controller instance.
  // When we pass this.handlePlay directly to addEventListener, the browser would call it with the 
  // video element as this, breaking our method.
  // Using bind(this) inside _bindHandlers() creates one bound function per handler, keeps the
  // right this value, and—crucially—lets us reuse the very same reference in removeEventListener 
  // during disconnect(). Without that, the teardown would fail because bind() returns a brand-new
  // function each time.
  _bindHandlers() {
    if (this._handlersBound) return;

    this._handlePlay = this.handlePlay.bind(this);
    this._handlePause = this.handlePause.bind(this);
    this._handleEnded = this.handlePause.bind(this);
    this._handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this._handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
    this._handleFullscreenChange = this.handleFullscreenChange.bind(this);
    this._handleWebkitBeginFullscreen = this.handleWebkitBeginFullscreen.bind(this);
    this._handleWebkitEndFullscreen = this.handleWebkitEndFullscreen.bind(this);
    this._handleSeeked = this.handleSeeked.bind(this);

    this._handlersBound = true;
  }

  _attachWebkitFullscreenHooks() {
    if (typeof this.videoTarget.webkitEnterFullscreen !== "function") return;

    this.videoTarget.addEventListener("webkitbeginfullscreen", this._handleWebkitBeginFullscreen);
    this.videoTarget.addEventListener("webkitendfullscreen", this._handleWebkitEndFullscreen);
  }

  _detachWebkitFullscreenHooks() {
    if (typeof this.videoTarget.webkitEnterFullscreen !== "function") return;

    this.videoTarget.removeEventListener("webkitbeginfullscreen", this._handleWebkitBeginFullscreen);
    this.videoTarget.removeEventListener("webkitendfullscreen", this._handleWebkitEndFullscreen);
  }
}
